import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Modal, Form, Input, Select,
  Popconfirm, message, Typography, Tag, Drawer, Spin, Row, Col, Affix, Tooltip, Divider
} from 'antd';
import {
  EditOutlined, DeleteOutlined, MailOutlined, SearchOutlined,
  CheckCircleOutlined, CloseCircleOutlined, UserOutlined, PlusOutlined,
  FileTextOutlined, SyncOutlined, FilterOutlined, ArrowLeftOutlined,
  ApartmentOutlined, TeamOutlined, DatabaseOutlined
} from '@ant-design/icons';
import { RefreshIcon } from '../../components/common/ActionIcons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import PageWrapper from '../../components/common/PageWrapper';

import {
  listBusinessOrganisations,
  createBusinessOrganisation,
  updateBusinessOrganisation,
  deleteBusinessOrganisation,
  activateBusinessOrganisation,
  deactivateBusinessOrganisation,
  getBusinessOrganisationDetails,
  loadBusinessOrganisationDto,
  sendEmail,
  listBusinessOrganisationsByIds
} from '../../actions/businessOrganisationActions';

import DynamicFormSP from '../../components/crud/DynamicFormSP.component';
import {
  tenantMigrationAllCounts,
  tenantMigrationSingleOutcome,
} from '../../utils/tenantMigrationSummary';

const { Text } = Typography;

const BusinessOrganisationDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [emailForm] = Form.useForm();

  const [state, setState] = useState({
    isDrawerVisible: false,
    editingOrganisation: null,
    emailModalVisible: false,
    selectedOrganisation: null,
    organisationListFilter: {
      query: '',
      page: 1,
      limit: 10,
      filter: 'name',
    },
    searchText: '',
    searchedColumn: '',
    searchProp: '',
    selectedRowKeys: [],
    isBulkDeleting: false,
  });

  const [emailSending, setEmailSending] = useState(false);
  const [migratingAll, setMigratingAll] = useState(false);
  const [migratingTenantId, setMigratingTenantId] = useState(null);

  const businessOrganisationList = useSelector(state => state.businessOrganisationList);
  const { loading, error, businessOrganisations, currentPage, totalPages, totalCount } = businessOrganisationList;

  const businessOrganisationDelete = useSelector(state => state.businessOrganisationDelete);
  const { loading: deleteLoading, success: deleteSuccess } = businessOrganisationDelete;

  const businessOrganisationActivate = useSelector(state => state.businessOrganisationActivate);
  const { loading: activateLoading, success: activateSuccess } = businessOrganisationActivate;

  const businessOrganisationDeactivate = useSelector(state => state.businessOrganisationDeactivate);
  const { loading: deactivateLoading, success: deactivateSuccess } = businessOrganisationDeactivate;

  useEffect(() => {
    dispatch(listBusinessOrganisations(state.organisationListFilter));
  }, [dispatch, state.organisationListFilter]);

  useEffect(() => {
    if (deleteSuccess || activateSuccess || deactivateSuccess) {
      dispatch(listBusinessOrganisations(state.organisationListFilter));
    }
  }, [deleteSuccess, activateSuccess, deactivateSuccess, dispatch]);

  const showDrawer = (organisation = null) => {
    setState(prev => ({ ...prev, editingOrganisation: organisation, isDrawerVisible: true }));
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteBusinessOrganisation(id));
      await handleDeletePrintFolders(id);
      message.success(t('Business organisation and associated print folders deleted successfully'));
      handleRefresh();
    } catch (error) {
      console.error('Error deleting business organisation:', error);
      message.error(t('Failed to delete business organisation or associated print folders'));
    }
  };

  const handleDeleteTemplates = async (organisationId) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      await axios.delete(`/print/documents/organisation/${organisationId}`, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });
      message.success(t('Templates deleted successfully'));
    } catch (error) {
      console.error('Error deleting templates:', error);
      message.error(t('Failed to delete templates'));
    }
  };
  
  const handleSyncTemplates = async (organisationId) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      await axios.post(`/print/documents/sync/${organisationId}`, null, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });
      message.success(t('Templates synced successfully'));
    } catch (error) {
      console.error('Error syncing templates:', error);
      message.error(t('Failed to sync templates'));
    }
  };

  const showEmailModal = (organisation) => {
    setState(prev => ({
      ...prev,
      selectedOrganisation: organisation,
      emailModalVisible: true
    }));
    emailForm.setFieldsValue({ to: organisation.contactEmail });
  };

  const handleSendEmail = (organisation) => {
    setEmailSending(true);
    emailForm.validateFields().then((values) => {
      dispatch(sendEmail({
        "EmailToId": values.to,
        "EmailToName": organisation.name,
        "EmailSubject": values.subject,
        "EmailBody": values.message
      }))
        .then(() => {
          setEmailSending(false);
          message.success(t('Email sent successfully'));
        })
        .catch((error) => {
          setEmailSending(false);
          message.error(t('Failed to send email: {{error}}', { error: error.message }));
        });
    }).catch(() => {
      setEmailSending(false);
    });
  };

  const generateEmailTemplate = (template) => {
    const { selectedOrganisation } = state;
    let subject = '';
    let message = '';
    switch (template) {
      case 'subscription_reminder':
        subject = t('Subscription Reminder');
        message = t('Dear {{name}},\n\nThis is a friendly reminder that your subscription is due to expire on {{date}}. Please renew your subscription to continue using our services.\n\nBest regards,\nThe OpenEP|NET Team', {
          name: selectedOrganisation.name,
          date: moment(selectedOrganisation.subscriptionExpiryDate).format('YYYY-MM-DD')
        });
        break;
      case 'payment_reminder':
        subject = t('Payment Reminder');
        message = t('Dear {{name}},\n\nWe noticed that your last payment was on {{date}}. Please ensure your payment information is up to date to avoid any service interruptions.\n\nBest regards,\nThe OpenEP|NET Team', {
          name: selectedOrganisation.name,
          date: moment(selectedOrganisation.lastPaymentDate).format('YYYY-MM-DD')
        });
        break;
      case 'welcome_email':
        subject = t('Welcome to OpenEP|NET');
        message = t('Dear {{name}},\n\nWelcome to OpenEP|NET! We\'re thrilled to have you on board. Your subscription package is {{package}}.\n\nIf you have any questions, feel free to reach out to us.\n\nBest regards,\nThe OpenEP|NET Team', {
          name: selectedOrganisation.name,
          package: selectedOrganisation.subscriptionPackage
        });
        break;
      default:
        break;
    }
    emailForm.setFieldsValue({ subject, message });
  };

  const handleDeletePrintFolders = async (organisationId) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      await axios.delete(`/print/documents/organisation/${organisationId}`, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        },
        data: { organisationId }
      });
      message.success(t('Print folders deleted successfully'));
    } catch (error) {
      console.error('Error deleting print folders:', error);
      message.error(t('Failed to delete print folders'));
    }
  };

  const handleMigrateAllTenants = async () => {
    try {
      setMigratingAll(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const { data } = await axios.post('/dotnet_api/v1/businessOrganisation/migrate/all', null, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });

      const { successCount, failedCount } = tenantMigrationAllCounts(data);
      message.success(t('Tenant migration completed. Success: {{success}}, Failed: {{failed}}', { success: successCount, failed: failedCount }));
    } catch (error) {
      console.error('Error migrating all tenants:', error);
      message.error(t('Failed to migrate all tenants'));
    } finally {
      setMigratingAll(false);
    }
  };

  const handleMigrateSingleTenant = async (organisationId) => {
    try {
      setMigratingTenantId(organisationId);
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const { data } = await axios.post(`/dotnet_api/v1/businessOrganisation/migrate/${organisationId}`, null, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });

      const outcome = tenantMigrationSingleOutcome(data);
      if (outcome.ok) {
        message.success(t('Tenant migration completed successfully'));
      } else {
        message.error(t('Tenant migration failed: {{error}}', { error: outcome.error }));
      }
    } catch (error) {
      console.error('Error migrating tenant:', error);
      message.error(t('Failed to migrate tenant database'));
    } finally {
      setMigratingTenantId(null);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`${t("Search")} ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t("Search")}
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            {t("Reset")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: text =>
      state.searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[state.searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Identifier'),
      dataIndex: 'identifier',
      key: 'identifier',
      ...getColumnSearchProps('identifier'),
    },
    
    {
      title: t('Subscription Status'),
      dataIndex: 'isSubscriptionActive',
      key: 'isSubscriptionActive',
      filters: [
        { text: t('Active'), value: 'true' },
        { text: t('Inactive'), value: 'false' },
      ],
      onFilter: (value, record) => record.isSubscriptionActive.toString() === value,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('Active') : t('Inactive')}
        </Tag>
      ),
    },
    {
      title: t('Expiry Date'),
      dataIndex: 'subscriptionExpiryDate',
      key: 'subscriptionExpiryDate',
      render: (date) => moment(date).format('YYYY-MM-DD'),
      sorter: (a, b) => moment(a.subscriptionExpiryDate).unix() - moment(b.subscriptionExpiryDate).unix(),
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => showDrawer(record)} />
          {!record.databaseConnectionString.includes("Database=mosaic_core;") && (
            <>
              <Popconfirm
                title={t("Are you sure you want to delete this business organisation?")}
                onConfirm={() => handleDelete(record.id)}
              >
                <Button icon={<DeleteOutlined />} danger loading={deleteLoading} />
              </Popconfirm>
              <Popconfirm
                title={record.isSubscriptionActive ? t("Are you sure you want to deactivate this business organisation?") : t("Are you sure you want to activate this business organisation?")}
                onConfirm={() => handleActivateDeactivate(record.id, record.isSubscriptionActive)}
              >
                <Button icon={record.isSubscriptionActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}>
                  {record.isSubscriptionActive ? t('Deactivate') : t('Activate')}
                </Button>
              </Popconfirm>
              <Popconfirm
                title={t("Are you sure you want to delete this organisation's templates?")}
                onConfirm={() => handleDeleteTemplates(record.id)}
              >
                <Button icon={<DeleteOutlined />} danger>
                {t('Delete Templates')}
                </Button>
              </Popconfirm>
            </>
          )}
          <Popconfirm
            title={t("Run migrations for this organisation's database?")}
            onConfirm={() => handleMigrateSingleTenant(record.id)}
          >
            <Button
              icon={<DatabaseOutlined />}
              loading={migratingTenantId === record.id}
            >
              {t('Migrate DB')}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={t("Are you sure you want to sync this organisation's templates?")}
            onConfirm={() => handleSyncTemplates(record.id)}
          >
            <Button icon={<SyncOutlined />}>
              {t('Sync Templates')}
            </Button>
          </Popconfirm>
          <Button icon={<MailOutlined />} onClick={() => showEmailModal(record)}>
            {t('Email')}
          </Button>
        </Space>
      ),
    },
  ];

  const handleFilterChange = (pagination, filters, sorter) => {
    setState(prev => ({
      ...prev,
      organisationListFilter: {
        ...prev.organisationListFilter,
        page: Number(pagination.current || 1),
        limit: Number(pagination.pageSize || prev.organisationListFilter.limit || 10),
      }
    }));
  };

  const handleRefresh = () => {
    setState(prev => ({
      ...prev,
      organisationListFilter: {
        query: '',
        page: 1,
        limit: 10,
        filter: 'name',
      },
      searchText: '',
      searchedColumn: '',
      searchProp: '',
      selectedRowKeys: [],
    }));
  };

  const executeStatisticsFunction = async (organisationId) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      await axios.post(`/businessOrganisations/statistics/ensure-function`, null, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });
      message.success(t('Statistics function executed successfully'));
    } catch (error) {
      console.error('Error executing statistics function:', error);
      message.error(t('Failed to execute statistics function'));
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setState(prev => ({
      ...prev,
      searchText: selectedKeys[0],
      searchedColumn: dataIndex,
      organisationListFilter: {
        ...prev.organisationListFilter,
        query: selectedKeys[0],
        filter: dataIndex,
        page: 1,
      }
    }));
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setState(prev => ({ ...prev, searchText: '' }));
  };

  const handleActivateDeactivate = (id, isActive) => {
    if (isActive) {
      dispatch(deactivateBusinessOrganisation(id));
    } else {
      dispatch(activateBusinessOrganisation(id));
    }
  };

  const handleSearchPropChange = (value) => {
    setState(prev => ({
      ...prev,
      searchProp: value,
      organisationListFilter: {
        ...prev.organisationListFilter,
        filter: value,
        page: 1,
      }
    }));
  };

  const handleBulkDelete = async () => {
    setState(prev => ({ ...prev, isBulkDeleting: true }));
    try {
      for (const key of state.selectedRowKeys) {
        await handleDelete(key);
      }
      setState(prev => ({ ...prev, selectedRowKeys: [], isBulkDeleting: false }));
      message.success(t('Selected items have been deleted.'));
      handleRefresh();
    } catch (error) {
      console.error(`Failed to delete some items:`, error);
      message.error(t('Failed to delete some items. Please try again.'));
      setState(prev => ({ ...prev, isBulkDeleting: false }));
    }
  };

  const tableTitle = () => {
    return (
      <div style={{ width: '100%' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={4} lg={3} xl={2}>
            <Text strong>
              <FilterOutlined /> {t("Filter")}:
            </Text>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5} xl={4}>
            <Select
              placeholder={t("Search by")}
              style={{ width: '100%' }}
              value={state.searchProp}
              onChange={handleSearchPropChange}
            >
              {columns.map((column) => (
                column.dataIndex && !/date/i.test(column.dataIndex) && (
                  <Select.Option key={column.dataIndex} value={column.dataIndex}>
                    {column.title}
                  </Select.Option>
                )
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10} lg={12} xl={14}>
            <Input.Search
              placeholder={`${t("Search by")} ${state.searchProp}`}
              onSearch={(value) => {
                setState(prev => ({
                  ...prev,
                  searchText: value,
                  organisationListFilter: {
                    ...prev.organisationListFilter,
                    query: value,
                    page: 1,
                  }
                }));
              }}
              value={state.searchText}
              onChange={(e) => setState(prev => ({ ...prev, searchText: e.target.value }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={24} md={4} lg={4} xl={4} style={{ textAlign: 'right' }}>
            <Button
              type="text"
              icon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              {t("Refresh")}
            </Button>
          </Col>
        </Row>
        {state.selectedRowKeys.length > 0 && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Row justify="center">
              <Col>
                <Popconfirm
                  title={t("Delete selected items")}
                  description={t("Are you sure you want to delete these items?")}
                  okText={t("Yes")}
                  cancelText={t("No")}
                  onConfirm={handleBulkDelete}
                >
                  <Button type="primary" danger loading={state.isBulkDeleting}>
                    {t("Delete")} ({state.selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  };
  
  const headerActions = (
    <Space wrap>
      <Button type="primary" onClick={() => showDrawer()} icon={<PlusOutlined />} size="small">
        {t('Add Organisation')}
      </Button>
      <Popconfirm
        title={t('Run migrations for all active tenant databases?')}
        onConfirm={handleMigrateAllTenants}
      >
        <Button
          icon={<DatabaseOutlined />}
          loading={migratingAll}
          size="small"
        >
          {t('Migrate All Tenants')}
        </Button>
      </Popconfirm>
    </Space>
  );

  return (
    <PageWrapper
      title={t('Business Organisation Dashboard')}
      icon={<ApartmentOutlined style={{ fontSize: 24 }} />}
      headerActions={headerActions}
      showCard={true}
      cardProps={{
        styles: {
          body: { padding: '12px' }
        }
      }}
    >
      <div style={{ margin: '-12px', padding: '12px' }}>
        <Table
          size='small'
          title={tableTitle}
          columns={columns}
          dataSource={businessOrganisations}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage ?? state.organisationListFilter.page,
            pageSize: state.organisationListFilter.limit,
            total: totalCount ?? businessOrganisations?.length ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleFilterChange}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: state.selectedRowKeys,
            onChange: (selectedRowKeys) => setState(prev => ({ ...prev, selectedRowKeys })),
          }}
        />
      </div>

      <Modal
        title={t("Send Email")}
        visible={state.emailModalVisible}
        onCancel={() => setState(prev => ({ ...prev, emailModalVisible: false }))}
        footer={[
          <Button key="close" onClick={() => setState(prev => ({ ...prev, emailModalVisible: false }))}>
            {t("Close")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => handleSendEmail(state.selectedOrganisation)}
            loading={emailSending}
            icon={<MailOutlined />}
          >
            {t("Send")}
          </Button>,
        ]}
      >
        <Spin spinning={emailSending}>
          <Form form={emailForm} layout="vertical">
            <Form.Item name="to" label={t("To")} rules={[{ required: true, message: t('Please input the recipient email!') }]}>
              <Input />
            </Form.Item>
            <Form.Item name="subject" label={t("Subject")} rules={[{ required: true, message: t('Please input the email subject!') }]}>
              <Input />
            </Form.Item>
            <Form.Item name="message" label={t("Message")} rules={[{ required: true, message: t('Please input the email message!') }]}>
              <Input.TextArea rows={4} />
            </Form.Item>
            <Space wrap>
              <Button onClick={() => generateEmailTemplate('subscription_reminder')}>{t("Subscription Reminder")}</Button>
              <Button onClick={() => generateEmailTemplate('payment_reminder')}>{t("Payment Reminder")}</Button>
              <Button onClick={() => generateEmailTemplate('welcome_email')}>{t("Welcome Email")}</Button>
            </Space>
          </Form>
        </Spin>
      </Modal>

      <Drawer
        title={state.editingOrganisation ? t("Edit Business Organisation") : t("Create Business Organisation")}
        placement="right"
        closable={false}
        onClose={() => setState(prev => ({ ...prev, isDrawerVisible: false }))}
        visible={state.isDrawerVisible}
        width={window.innerWidth > 768 ? 840 : '100%'}
      >
        <DynamicFormSP
          key={state.editingOrganisation ? state.editingOrganisation.id : 'new'}
          currentEntityId={state.editingOrganisation ? state.editingOrganisation.id : null}
          defaultEditMode={state.editingOrganisation ? "Edit" : "Create"}
          singularEntity="businessOrganisation"
          pluralEntity="businessOrganisations"
          constantPrefix="BUSINESS_ORGANISATION"
          loadDto={loadBusinessOrganisationDto}
          createEntity={createBusinessOrganisation}
          updateEntity={updateBusinessOrganisation}
          getEntityDetails={getBusinessOrganisationDetails}
          listEntities={listBusinessOrganisations}
          listEntitiesByIds={listBusinessOrganisationsByIds}
          onUpdatedEntityChange={(updatedEntity) => {
            // Handle updated entity if needed
          }}
          onCreatedEntityChange={(createdEntity) => {
            // Execute statistics function for new organisation
            if (createdEntity && createdEntity.id) {
              executeStatisticsFunction(createdEntity.id);
            }
          }}
          isParent={true}
          fieldsToHide={['Id', 'DSAStatus', 'DPAStatus', 'TOCStatus', 'DSASignedDate', 'DPASignedDate', 'TOCSignedDate', 'DSASignedBy', 'DPASignedBy', 'TOCSignedBy', 'DSADocumentJson', 'DPADocumentJson', 'TOCDocumentJson', 'CurrencySymbol', 'CurrencyCode']}
          summaryProps={['name', 'identifier', 'subscriptionPackage', 'isSubscriptionActive']}
          enableCreate={true}
          enableSearchBar={false}
          enableActionButtons={false}
        />
      </Drawer>
    </PageWrapper>
  );
};

export default BusinessOrganisationDashboard;